import type { AlertBannerProps } from '@/components/molecules/AlertBanner';
import type {
  BillingUsageBanner,
  BillingUsageItem,
  UsageBannerKind,
  UsageLimitCode,
  UsageTier,
  WorkspaceDaily,
} from '@/lib/billing';

/**
 * Non-component domain logic for TRU-540's four-stage usage nudges. The
 * tier→tone maps, the per-limit label, the count/percent copy and the CTA target
 * all live here so every `.tsx` in this feature stays component-only (Vite Fast
 * Refresh) and each rule is unit-testable without rendering.
 *
 * Mirrors `billing-banner.ts`, and splits responsibility the same way: the
 * BACKEND owns WHICH tier a limit is in (`USAGE_TIER_THRESHOLDS` in
 * `usage-nudge.constants.ts`) and which limit wins the banner. Nothing here
 * re-derives a tier from a percentage — doing so would fork the thresholds
 * across two codebases and let the meter disagree with the banner above it.
 *
 * Every map below is an exhaustive `Record` over the union in `lib/billing.ts`,
 * so adding a fifth tier is a compile error at each call site rather than a
 * silently untoned meter.
 */

/**
 * Tier → `AlertBanner` variant, so the tones stay the design system's.
 *
 * `APPROACHING` is `null`: 70% is the SUBTLE INDICATOR tier by product
 * definition — it tints the meter and nothing else. The backend already enforces
 * this by emitting no `usageBanner` for it; the null here is what makes the same
 * rule true for any client-side surface that maps a tier to a banner tone.
 *
 * `CRITICAL` and `REACHED` share `error` (the red banner in the spec) because
 * red is the loudest tone the palette has — the two are distinguished by copy
 * and by the 100% modal, not by a third colour.
 */
export const USAGE_TIER_VARIANT: Record<
  UsageTier,
  NonNullable<AlertBannerProps['variant']> | null
> = {
  APPROACHING: null,
  WARNING: 'warning',
  CRITICAL: 'error',
  REACHED: 'error',
};

/**
 * Banner kind → `AlertBanner` variant. Derived from the same two tones as
 * `USAGE_TIER_VARIANT` rather than aliasing it: the server sends a `kind` on the
 * banner and a `tier` on the usage item, and this map is the one that renders
 * the banner. There is no `USAGE_APPROACHING` kind to map.
 */
export const USAGE_BANNER_VARIANT: Record<
  UsageBannerKind,
  NonNullable<AlertBannerProps['variant']>
> = {
  USAGE_WARNING: 'warning',
  USAGE_CRITICAL: 'error',
  USAGE_REACHED: 'error',
};

/**
 * Tier → the meter's fill class, as a Tailwind arbitrary variant targeting the
 * Radix `Progress` indicator (`[&>div]`) — the idiom `/design` already uses to
 * retint that primitive, so no fork of `components/ui/progress.tsx` is needed.
 *
 * Semantic tokens only. Below 70% there is no tier and the meter keeps the
 * primitive's own `bg-brand` default — see `usageMeterClass`.
 */
export const USAGE_TIER_METER_CLASS: Record<UsageTier, string> = {
  APPROACHING: '[&>div]:bg-warning',
  WARNING: '[&>div]:bg-warning',
  CRITICAL: '[&>div]:bg-destructive',
  REACHED: '[&>div]:bg-destructive',
};

/**
 * Tier → the short badge shown beside a meter. Distinct from the banner copy:
 * this has to fit in a 200px sidebar rail, so it names the STATE rather than
 * repeating the numbers already on the line above it.
 */
export const USAGE_TIER_LABEL: Record<UsageTier, string> = {
  APPROACHING: 'Approaching limit',
  WARNING: 'Nearing limit',
  CRITICAL: 'Almost at limit',
  REACHED: 'Limit reached',
};

/**
 * The same tiers, scoped for a meter that shows something OTHER than the org
 * total — the sidebar's per-workspace budget. There the tier still comes from
 * the org-wide figure, so an unscoped "Limit reached" under "0 / 10 emails"
 * reads as a contradiction (another workspace spent the org's budget).
 */
export const USAGE_TIER_ORG_LABEL: Record<UsageTier, string> = {
  APPROACHING: 'Approaching org-wide limit',
  WARNING: 'Nearing org-wide limit',
  CRITICAL: 'Almost at org-wide limit',
  REACHED: 'Org-wide limit reached',
};

/**
 * User-facing noun per limit, in the sentence-initial casing the cards and the
 * dialog title need. The backend has its own mid-sentence lowercase copy
 * ("connected mailboxes") baked into `usageBanner.message`; these are a
 * different grammatical form for a different position, not a duplicate of it.
 */
export const USAGE_LIMIT_LABEL: Record<UsageLimitCode, string> = {
  EMAIL_ACCOUNTS: 'Connected mailboxes',
  DAILY_EMAILS: 'Daily warmup emails',
};

/**
 * The CTA is Upgrade, always, for every tier and both limits — this product has
 * no add-on/pack purchase. `/billing` is owner-gated (it is hidden from the
 * sidebar for members and renders a "managed by your owner" banner), which is
 * why `usageNudgeCtaVisible` exists rather than every surface hard-coding it.
 */
export const USAGE_UPGRADE_ROUTE = '/billing' as const;
export const USAGE_UPGRADE_LABEL = 'Upgrade plan';

/**
 * Whether to offer the Upgrade CTA to this viewer.
 *
 * `false` for a non-owner on every surface, mirroring `billingBannerAction`:
 * `/billing` is owner-gated, so a member who clicks it lands on a page that only
 * tells them to ask their owner. They get that instruction in the copy instead.
 */
export function usageNudgeCtaVisible(isOrgOwner: boolean): boolean {
  return isOrgOwner;
}

/** The entry for `code`, or `null` when the summary carried no usage at all. */
export function findUsage(
  usage: readonly BillingUsageItem[] | undefined,
  code: UsageLimitCode,
): BillingUsageItem | null {
  return usage?.find((item) => item.code === code) ?? null;
}

/** `true` when the plan places no cap on this limit (`limit === null`). */
export function isUsageUnlimited(item: BillingUsageItem): boolean {
  return item.limit === null;
}

/**
 * The meter's 0–100 value. Uses the SERVER's `pct` (already rounded and capped
 * at 100) rather than recomputing `used / limit`, so the bar can never disagree
 * with the percentage in the banner. An unlimited limit has no percentage, so it
 * reads 0 — the surface renders an "Unlimited" label instead of the bar.
 */
export function usageMeterValue(item: BillingUsageItem): number {
  return item.pct ?? 0;
}

/**
 * The meter fill class for an item — the tier's tone, or the primitive's default
 * brand fill below 70% (and always when unlimited, where `tier` is null).
 */
export function usageMeterClass(item: BillingUsageItem): string {
  return item.tier === null ? '' : USAGE_TIER_METER_CLASS[item.tier];
}

/**
 * The current workspace's daily usage as a meter item, or null when its budget
 * or its sent count is unknown (never drawn as 0). No tier: nudges are org-level.
 */
export function workspaceDailyUsageItem(ws: WorkspaceDaily | null): BillingUsageItem | null {
  if (!ws || ws.limit === null || ws.used === null) return null;
  const pct = ws.limit === 0 ? (ws.used > 0 ? 100 : 0) : Math.min(100, Math.round((ws.used / ws.limit) * 100));
  return { code: 'DAILY_EMAILS', used: ws.used, limit: ws.limit, pct, tier: null };
}

/**
 * The "17 of 20" / "17 used" line under a limit's name.
 *
 * An unlimited limit renders its raw consumption with no denominator — never
 * "17 of 0" and never a full bar, which is what coercing a null limit to 0 would
 * produce.
 */
export function usageCountLabel(item: BillingUsageItem): string {
  return item.limit === null
    ? `${item.used} used`
    : `${item.used} of ${item.limit}`;
}

/** "Unlimited" for a null limit, else the server's capped percentage. */
export function usagePercentLabel(item: BillingUsageItem): string {
  return item.pct === null ? 'Unlimited' : `${item.pct}%`;
}

/**
 * The banner's sentence for this viewer.
 *
 * The OWNER gets `banner.message` verbatim — it is server-authored, carries no
 * raw ISO date, and its "Upgrade your plan to add more" instruction is one they
 * can actually act on. This is the opposite call to `billingBannerMessage`,
 * which replaces the server copy wholesale; the difference is that the billing
 * banner's server string embeds a raw `2026-07-27` date, and this one doesn't.
 *
 * A MEMBER gets a client-composed sentence built from the same structured
 * fields, because the server's imperative is wrong for them: `/billing` is
 * owner-gated, so "Upgrade your plan" is an instruction they cannot follow.
 */
export function usageBannerMessage(
  banner: BillingUsageBanner,
  isOrgOwner: boolean,
): string {
  if (isOrgOwner) return banner.message;

  const label = USAGE_LIMIT_LABEL[banner.code].toLowerCase();
  const counts = `(${banner.used} of ${banner.limit})`;
  const lead =
    banner.kind === 'USAGE_REACHED'
      ? `Your organization has reached its ${label} limit ${counts}.`
      : `Your organization has used ${banner.pct}% of its ${label} ${counts}.`;
  return `${lead} Ask your workspace owner to upgrade for a higher limit.`;
}

/**
 * The 100%-modal body for this viewer.
 *
 * Deliberately does NOT promise the action is blocked client-side: the server is
 * the only authority on the limit (adding an account past `maxEmailAccounts`
 * already throws), and this summary is cached for 60s, so an org that upgraded a
 * moment ago could see this modal while the server would happily accept the
 * account. The copy therefore explains and offers the upgrade — the surface
 * still lets the user continue and take the server's answer.
 */
export function usageLimitDialogMessage(
  item: BillingUsageItem,
  isOrgOwner: boolean,
): string {
  const label = USAGE_LIMIT_LABEL[item.code].toLowerCase();
  const counts = item.limit === null ? '' : ` (${item.used} of ${item.limit})`;
  const lead = `You've reached your plan's limit for ${label}${counts}.`;
  return isOrgOwner
    ? `${lead} Upgrade your plan to add more. Otherwise this will be rejected.`
    : `${lead} Ask your workspace owner to upgrade. Otherwise this will be rejected.`;
}

/**
 * `true` only when this limit is genuinely at 100%. A missing entry (the schema
 * degraded `usage` to `[]`, or an older backend omitted it) is `false`: an
 * absent nudge must never gate a surface that the server would have allowed.
 */
export function isUsageReached(
  usage: readonly BillingUsageItem[] | undefined,
  code: UsageLimitCode,
): boolean {
  return findUsage(usage, code)?.tier === 'REACHED';
}
